import React, { useEffect, useState } from 'react'

import { RxExternalLink } from '@react-icons/all-files/rx/RxExternalLink'

import type { SourceCatalog } from 'types/track'

import { Flex, HoverCard, Text } from 'components/ds'

import styles from './catalog-info-popover.module.css'

const isHttpsUrl = (value: string | undefined): value is string => {
  if (!value) return false
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

interface CatalogInfoPopoverProps {
  source: SourceCatalog
  children: React.ReactElement
}

export const CatalogImageOrFallback = ({
  imageUrl,
  name,
  className,
}: {
  imageUrl?: string | null
  name: string
  className?: string
}) => {
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setHasError(false)
  }, [imageUrl])

  if (imageUrl && !hasError) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={`${styles.catalogImage} ${className ?? ''}`}
        onError={() => setHasError(true)}
      />
    )
  }

  return (
    <div
      aria-hidden="true"
      className={`${styles.catalogImageFallback} ${className ?? ''}`}
    >
      <span>{name.charAt(0).toUpperCase()}</span>
    </div>
  )
}

export const CatalogInfoPopover = ({
  source,
  children,
}: CatalogInfoPopoverProps) => {
  const catalogName = source.name?.trim() || source.business_name?.trim()
  const name = catalogName || 'Untitled catalog'
  return (
    <HoverCard.Root openDelay={300} closeDelay={100}>
      <HoverCard.Trigger data-testid="catalog-info-popover">
        {children}
      </HoverCard.Trigger>
      <HoverCard.Content side="bottom" align="start" className={styles.content}>
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <CatalogImageOrFallback imageUrl={source.image_url} name={name} />
            <Text
              size="3"
              weight="bold"
              translate={catalogName ? 'no' : undefined}
            >
              {name}
            </Text>
          </Flex>

          {source.description && (
            <Text size="2" className={styles.description} translate="no">
              {source.description}
            </Text>
          )}

          {source.track_count != null && (
            <Text size="2" className={styles.description}>
              {`${source.track_count.toLocaleString('en-US')} ${
                source.track_count === 1 ? 'track' : 'tracks'
              }`}
            </Text>
          )}

          {isHttpsUrl(source.url) && (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.visitLink}
              onClick={e => e.stopPropagation()}
            >
              <RxExternalLink aria-hidden="true" />
              <Text size="2">Visit catalog</Text>
            </a>
          )}
        </Flex>
      </HoverCard.Content>
    </HoverCard.Root>
  )
}
